import React from "react";
import { Card, CardContent } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import {
  Target, FileText, Eye, ArrowRight, TrendingUp, CheckCircle2,
  Briefcase, Megaphone,
} from "lucide-react";

/*
  CedExecutiveBanner — Renders the CED-specific executive sections at the top
  of an assessment report when result.industry === "community_economic_development".

  Surfaces the spec Section K result page requirements:
    1. Executive Summary
    2. Top 3 Workflow Ranking
    3. Funding Readiness Snapshot
    4. Executive Visibility Snapshot
    5. Why This Matters Now
    6. Recommended 60–90 Day Pilot Path (in-depth only)
*/

const PILLAR_LABELS = {
  ced_funding_pursuit:      "Funding Pursuit",
  ced_impact_reporting:     "Impact Reporting",
  ced_executive_visibility: "Executive Visibility",
  ced_workforce_ops:        "Workforce / Resident Ops",
  ced_community_signals:    "Community Signals",
  ced_governance_readiness: "Governance & Readiness",
  ced_mobility_ops:         "Mobility / Field Ops",
};

const WORKFLOW_ICON = {
  "Grant & RFP Command Center": Briefcase,
  "Impact Reporting & Funder Proof": FileText,
  "Executive Visibility Dashboard": Eye,
  "Workforce / Resident Intake, Matching & Follow-Up Automation": Target,
  "Community Signal Analysis & Intervention Prioritization": Megaphone,
  "Mobility / Service Delivery Optimization": ArrowRight,
};

const WORKFLOW_BLURB = {
  "Grant & RFP Command Center":
    "High-value opportunity to improve how grants, RFPs, renewals, and contract opportunities are identified, prioritized, tracked, and prepared. AI can reduce manual drafting and follow-up burden while improving submission readiness and reuse of strong prior materials.",
  "Impact Reporting & Funder Proof":
    "Critical opportunity to strengthen impact reporting and funder proof. AI can help organize outcome evidence, reduce manual reporting effort, and improve your ability to show what changed, what was achieved, and what supports renewal or expansion.",
  "Executive Visibility Dashboard":
    "Strong opportunity to improve executive visibility and operational oversight. AI-enabled dashboards help leadership see deadlines, owners, risks, program status, and reporting readiness before issues fall through the cracks.",
  "Workforce / Resident Intake, Matching & Follow-Up Automation":
    "Strong candidate for service workflow automation. AI can reduce administrative drag, improve case flow, and help staff spend more time on direct service rather than documentation.",
  "Community Signal Analysis & Intervention Prioritization":
    "High-priority opportunity to improve community signal analysis. AI can turn meetings, surveys, outreach, and partner feedback into structured, searchable signal — making recurring needs, geographic patterns, and intervention priorities visible.",
  "Mobility / Service Delivery Optimization":
    "Operational opportunity to turn trip, routing, and field activity data into usable demand, equity, and access insight — planning and improving service on evidence rather than intuition.",
};

const PILOT_PATH_STEPS = [
  "Review current grant, RFP, and reporting workflows",
  "Identify highest-value automation opportunities",
  "Design a controlled AI source library",
  "Define Grant & RFP Command Center requirements",
  "Define Impact Reporting & Funder Proof requirements",
  "Define Executive Visibility Dashboard requirements",
  "Create initial AI playbooks",
  "Produce roadmap, KPIs, and next-step recommendations",
];

const scoreBand = (score) => {
  // Score is 1-5 capability; convert to opportunity language.
  if (score == null) return { label: "—", color: "text-[#6b7280]" };
  if (score >= 4.0) return { label: "Strong current capability", color: "text-[#059669]" };
  if (score >= 3.0) return { label: "Moderate opportunity",      color: "text-[#b45309]" };
  if (score >= 2.0) return { label: "High-value opportunity",    color: "text-[#b91c1c]" };
  return { label: "Critical opportunity",                        color: "text-[#7f1d1d]" };
};

const CedExecutiveBanner = ({ result, textBase = "text-base", textSm = "text-sm" }) => {
  const cedPayload = result?.ced_payload || {};
  const pillarMap = cedPayload.pillar_score_map || {};
  const workflowsRanked = (cedPayload.workflows_ranked || []).filter((w) => !w.suppressed);
  const top3 = workflowsRanked.slice(0, 3);
  const flags = cedPayload.scoping_flags || {};
  const isInDepth = result?.assessment_type !== "initial";
  const migrated = cedPayload.migrated_from_v1;

  // Pillar accessors (default 3.0 = neutral when missing)
  const funding = pillarMap.ced_funding_pursuit ?? null;
  const impact  = pillarMap.ced_impact_reporting ?? null;
  const exec    = pillarMap.ced_executive_visibility ?? null;
  const gov     = pillarMap.ced_governance_readiness ?? null;

  const fundingBand = scoreBand(funding);
  const impactBand  = scoreBand(impact);
  const execBand    = scoreBand(exec);

  if (!cedPayload || !cedPayload.industry) return null;

  return (
    <div className="space-y-6 mb-8" data-testid="ced-executive-banner">
      {migrated && (
        <div className="rounded-lg border border-[#fde68a] bg-[#fffbeb] p-3 text-[#92400e] text-sm" data-testid="ced-migration-notice">
          <strong>Updated analysis.</strong> This assessment has been re-scored under our revised
          Community &amp; Economic Development workflow model — dominant outcomes now centre on
          funding pursuit, impact proof, and executive visibility.
        </div>
      )}

      {/* ── Executive Summary card ── */}
      <Card className="border border-[#e5e7eb] bg-white" data-testid="ced-executive-summary">
        <CardContent className="p-6">
          <div className="flex items-center gap-2 mb-3">
            <Target className="h-5 w-5 text-[#1a56db]" />
            <h2 className="text-lg font-bold text-[#111827]">Executive Summary</h2>
          </div>
          <p className={`${textBase} text-[#374151] leading-relaxed`}>
            Your results suggest the highest-leverage AI opportunities are in <strong>funding
            pursuit</strong>, <strong>impact proof</strong>, and <strong>executive visibility</strong>.
            These areas typically offer the strongest immediate value because they affect
            renewals, fundraising readiness, leadership oversight, and cross-program execution.
          </p>
          {cedPayload.readiness_note && (
            <div className="mt-3 text-sm text-[#6b7280] italic">
              Sequencing note: {cedPayload.readiness_note}
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── Top 3 Workflow Ranking ── */}
      {top3.length > 0 && (
        <div data-testid="ced-top-workflows">
          <div className="flex items-center gap-2 mb-3">
            <TrendingUp className="h-5 w-5 text-[#1a56db]" />
            <h2 className="text-lg font-bold text-[#111827]">Top {top3.length} AI Workflow Opportunities</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {top3.map((w, i) => {
              const Icon = WORKFLOW_ICON[w.workflow_name] || Target;
              return (
                <Card key={w.id} className="border border-[#e5e7eb] bg-white" data-testid={`ced-top-workflow-${i + 1}`}>
                  <CardContent className="p-5">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-[#1a56db] text-white text-xs font-bold">
                          {i + 1}
                        </span>
                        <Icon className="h-4 w-4 text-[#1a56db]" />
                      </div>
                      {w.opportunity_band && (
                        <Badge className="bg-[#eff6ff] text-[#1a56db] border-[#bfdbfe] text-[10px]">
                          {w.opportunity_band}
                        </Badge>
                      )}
                    </div>
                    <h3 className="text-[15px] font-bold text-[#111827] mb-2 leading-tight">
                      {w.workflow_name}
                    </h3>
                    <p className={`${textSm} text-[#4b5563] leading-relaxed`}>
                      {WORKFLOW_BLURB[w.workflow_name] || w.outcome}
                    </p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Funding Readiness + Executive Visibility Snapshots side-by-side ── */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className="border border-[#e5e7eb] bg-white" data-testid="ced-funding-snapshot">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-3">
              <Briefcase className="h-5 w-5 text-[#1a56db]" />
              <h3 className="text-base font-bold text-[#111827]">Funding Readiness Snapshot</h3>
            </div>
            <p className={`${textSm} text-[#4b5563] leading-relaxed mb-4`}>
              Your responses suggest AI may help strengthen how your organization pursues funding,
              proves value, and prepares renewal or expansion conversations. The strongest gains
              are likely to come from better opportunity tracking, stronger evidence organization,
              and improved executive visibility.
            </p>
            <div className="space-y-2">
              <SnapshotRow
                label={PILLAR_LABELS.ced_funding_pursuit}
                score={funding}
                band={fundingBand}
              />
              <SnapshotRow
                label={PILLAR_LABELS.ced_impact_reporting}
                score={impact}
                band={impactBand}
              />
              {flags.scope_funding_priority && (
                <div className="text-xs text-[#1a56db] flex items-center gap-1.5 mt-2">
                  <CheckCircle2 className="h-3.5 w-3.5" />
                  Grants &amp; renewals flagged as a major organizational priority.
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        <Card className="border border-[#e5e7eb] bg-white" data-testid="ced-exec-visibility-snapshot">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-3">
              <Eye className="h-5 w-5 text-[#1a56db]" />
              <h3 className="text-base font-bold text-[#111827]">Executive Visibility Snapshot</h3>
            </div>
            <p className={`${textSm} text-[#4b5563] leading-relaxed mb-4`}>
              These responses reflect whether leadership has one current view, whether risks and
              deadlines are visible early enough, and whether important knowledge is trapped
              in people or files.
            </p>
            <div className="space-y-2">
              <SnapshotRow
                label={PILLAR_LABELS.ced_executive_visibility}
                score={exec}
                band={execBand}
              />
              <SnapshotRow
                label={PILLAR_LABELS.ced_governance_readiness}
                score={gov}
                band={scoreBand(gov)}
              />
              {flags.scope_exec_visibility && (
                <div className="text-xs text-[#1a56db] flex items-center gap-1.5 mt-2">
                  <CheckCircle2 className="h-3.5 w-3.5" />
                  Leadership would benefit from one current view of deadlines, owners, and risks.
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* ── Why This Matters Now ── */}
      <Card className="border border-[#e5e7eb] bg-[#f9fafb]" data-testid="ced-why-this-matters">
        <CardContent className="p-5">
          <h3 className="text-base font-bold text-[#111827] mb-2">Why This Matters Now</h3>
          <p className={`${textSm} text-[#4b5563] leading-relaxed`}>
            Organizations in this sector often depend on grants, renewals, partner funding, and
            strong reporting discipline. When proposal work, outcome proof, and executive visibility
            are too manual, it becomes harder to scale what works and easier for important details
            to fall through the cracks.
          </p>
        </CardContent>
      </Card>

      {/* ── 60-90 Day Pilot Path — only on In-depth results ── */}
      {isInDepth && (
        <Card className="border border-[#e5e7eb] bg-white" data-testid="ced-pilot-path">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-3">
              <ArrowRight className="h-5 w-5 text-[#1a56db]" />
              <h3 className="text-base font-bold text-[#111827]">Recommended 60–90 Day Pilot Path</h3>
            </div>
            <ol className="space-y-2 list-decimal pl-5">
              {PILOT_PATH_STEPS.map((step, idx) => (
                <li key={idx} className={`${textSm} text-[#374151] leading-relaxed`}>
                  {step}
                </li>
              ))}
            </ol>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

const SnapshotRow = ({ label, score, band }) => (
  <div className="flex items-center justify-between py-1.5 border-b border-[#f3f4f6] last:border-0">
    <span className="text-sm text-[#374151]">{label}</span>
    <div className="flex items-center gap-2">
      <span className="text-sm font-semibold tabular-nums text-[#111827]">
        {score != null ? `${score.toFixed(1)} / 5.0` : "—"}
      </span>
      <span className={`text-[10px] uppercase tracking-wider font-semibold ${band.color}`}>
        {band.label}
      </span>
    </div>
  </div>
);

export default CedExecutiveBanner;
